import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Lock,
    ArrowRight,
    ShieldCheck,
    Mail,
    User as UserIcon,
} from "lucide-react";
import { redirectToGoogleLogin, useAuth, API } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputClass =
    "bg-[#0A0A0B] border-white/10 rounded-sm text-[#F4F4F5] placeholder:text-[#A1A1AA]/60 h-11 mt-2";

function formatApiError(detail) {
    if (!detail) return "Något gick fel.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function Login() {
    const { user, loading, setUser } = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState("login"); // "login" | "register"
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!loading && user) {
            navigate(user.role === "admin" ? "/admin" : "/portal", {
                replace: true,
            });
        }
    }, [user, loading, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email || !form.password || (mode === "register" && !form.name)) {
            toast.error("Fyll i alla fält");
            return;
        }
        if (form.password.length < 8) {
            toast.error("Lösenordet måste vara minst 8 tecken");
            return;
        }
        setSubmitting(true);
        try {
            const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
            const payload =
                mode === "login"
                    ? { email: form.email, password: form.password }
                    : { name: form.name, email: form.email, password: form.password };
            const { data } = await axios.post(`${API}${endpoint}`, payload, {
                withCredentials: true,
            });
            setUser(data.user);
            toast.success(mode === "login" ? "Välkommen tillbaka!" : "Konto skapat!");
            navigate(data.user.role === "admin" ? "/admin" : "/portal", {
                replace: true,
            });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Inloggningen misslyckades");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            data-testid="page-login"
            className="relative min-h-[85vh] grid place-items-center py-20 overflow-hidden"
        >
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/3 left-1/3 w-[420px] h-[420px] bg-[#C4A977]/[0.07] rounded-full blur-3xl" />
            </div>
            <div className="relative max-w-lg w-full mx-4 bg-[#111113] border border-white/10 rounded-sm p-10 lg:p-14">
                <div className="w-14 h-14 border border-[#C4A977]/40 grid place-items-center text-[#C4A977]">
                    <Lock size={22} />
                </div>
                <p className="mt-6 overline">Klientportal</p>
                <h1 className="mt-3 font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
                    {mode === "login" ? (
                        <>
                            Logga in för att följa{" "}
                            <span className="italic text-[#C4A977]">ditt ärende</span>.
                        </>
                    ) : (
                        <>
                            Skapa ditt{" "}
                            <span className="italic text-[#C4A977]">klientkonto</span>.
                        </>
                    )}
                </h1>

                {/* Email + Password Form */}
                <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
                    {mode === "register" && (
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                                Namn
                            </Label>
                            <div className="relative">
                                <UserIcon
                                    size={14}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 mt-1 text-[#A1A1AA]"
                                />
                                <Input
                                    data-testid="auth-name"
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm({ ...form, name: e.target.value })
                                    }
                                    className={`${inputClass} pl-10`}
                                    placeholder="Anna Andersson"
                                    autoComplete="name"
                                    required
                                />
                            </div>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                            E-post
                        </Label>
                        <div className="relative">
                            <Mail
                                size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 mt-1 text-[#A1A1AA]"
                            />
                            <Input
                                data-testid="auth-email"
                                type="email"
                                value={form.email}
                                onChange={(e) =>
                                    setForm({ ...form, email: e.target.value })
                                }
                                className={`${inputClass} pl-10`}
                                placeholder="namn@bmjuridik.se"
                                autoComplete="email"
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">
                            Lösenord
                        </Label>
                        <div className="relative">
                            <Lock
                                size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 mt-1 text-[#A1A1AA]"
                            />
                            <Input
                                data-testid="auth-password"
                                type="password"
                                value={form.password}
                                onChange={(e) =>
                                    setForm({ ...form, password: e.target.value })
                                }
                                className={`${inputClass} pl-10`}
                                placeholder={mode === "register" ? "Minst 8 tecken" : "••••••••"}
                                autoComplete={mode === "login" ? "current-password" : "new-password"}
                                minLength={8}
                                required
                            />
                        </div>
                    </div>
                    <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="auth-submit"
                        className="w-full bg-[#C4A977] hover:bg-[#DBC08F] text-black rounded-sm h-11 text-sm tracking-wide font-medium"
                    >
                        {submitting
                            ? "Vänta..."
                            : mode === "login"
                              ? "Logga in"
                              : "Skapa konto"}
                    </Button>
                </form>

                <div className="mt-5 text-center text-sm text-[#A1A1AA]">
                    {mode === "login" ? (
                        <>
                            <Link
                                to="/glomt-losenord"
                                data-testid="forgot-password-link"
                                className="text-[#A1A1AA] hover:text-[#C4A977] block mb-3"
                            >
                                Glömt lösenord?
                            </Link>
                            Inget konto?{" "}
                            <button
                                onClick={() => setMode("register")}
                                data-testid="switch-to-register"
                                className="text-[#C4A977] hover:underline"
                            >
                                Skapa konto
                            </button>
                        </>
                    ) : (
                        <>
                            Har du redan konto?{" "}
                            <button
                                onClick={() => setMode("login")}
                                data-testid="switch-to-login"
                                className="text-[#C4A977] hover:underline"
                            >
                                Logga in
                            </button>
                        </>
                    )}
                </div>

                {/* Divider */}
                <div className="mt-8 flex items-center gap-3 text-xs text-[#A1A1AA]/60">
                    <span className="flex-1 h-px bg-white/10" />
                    eller
                    <span className="flex-1 h-px bg-white/10" />
                </div>

                {/* Google Auth */}
                <button
                    data-testid="login-google-button"
                    onClick={() => redirectToGoogleLogin("/portal")}
                    type="button"
                    className="mt-6 w-full inline-flex items-center justify-center gap-3 px-6 py-3 border border-white/10 hover:border-[#C4A977]/60 hover:text-[#C4A977] transition-colors rounded-sm text-sm tracking-wide group"
                >
                    Fortsätt med Google
                    <ArrowRight
                        size={14}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                </button>

                <div className="mt-8 flex items-start gap-3 text-xs text-[#A1A1AA]/80">
                    <ShieldCheck
                        size={14}
                        className="text-[#C4A977] mt-0.5 shrink-0"
                    />
                    <p>
                        Vi krypterar lösenord enligt branschstandard (bcrypt) och
                        skyddar mot brute-force-attacker. Läs mer i vår{" "}
                        <Link to="/integritetspolicy" className="underline hover:text-[#C4A977]">
                            integritetspolicy
                        </Link>
                        .
                    </p>
                </div>
            </div>
        </section>
    );
}
